/**
 * Test records: mandatory testing, as recorded on the platform (`/v1/test-records`).
 *
 * The platform judges a test when it is recorded and keeps the thresholds it
 * judged against. This module only reads that back, searches it, and says it
 * in words — it never re-judges a record against today's thresholds, because a
 * verdict an auditor has read must not change under them.
 */
import { t } from "../i18n";

export const OUTCOMES = ["fail", "attention", "pass", "not_assessed"] as const;
export type Outcome = (typeof OUTCOMES)[number];

export const OUTCOME_LABEL: Record<Outcome, string> = {
  fail: "Action needed",
  attention: "Needs attention",
  pass: "Pass",
  not_assessed: "Not assessed",
};

export type TestMode = "threshold" | "ratio" | "judged";
export type DueState = "overdue" | "due_soon" | "current" | "no_interval" | "never_tested";

export const DUE_LABEL: Record<DueState, string> = {
  overdue: "Overdue",
  due_soon: "Due within 30 days",
  current: "Current",
  no_interval: "No interval set",
  never_tested: "Never tested",
};

export interface Threshold {
  label: string;
  match?: Record<string, string> | null;
  attention?: number | null;
  fail?: number | null;
  basis?: "value" | "pct_of_specified";
  higherIsBetter?: boolean;
  status: "proposed" | "agreed";
  basisNote?: string;
}

export interface SummaryField {
  key: string;
  label: string;
  type: "text" | "number";
}

export interface TestType {
  key: string;
  name: string;
  description?: string;
  frameworks: string[];
  mode: TestMode;
  measurementKey?: string;
  unit?: string;
  intervalDays?: number;
  intervalStatus: "proposed" | "agreed";
  thresholds: Threshold[];
  ratio?: {
    value: string;
    valueLabel?: string;
    limit: string;
    limitLabel?: string;
    status: "proposed" | "agreed";
    basisNote?: string;
  };
  summaryFields: SummaryField[];
  /** build 6B: who last confirmed or changed the thresholds, and when */
  updatedAt?: string;
  updatedBy?: string;
}

export interface TestRecord {
  id: string;
  reference: string;
  testType: string;
  testTypeName: string;
  frameworks: string[];
  title: string;
  performedOn: string;
  performedBy?: string;
  method?: string;
  device?: string;
  scope?: string;
  summary: Record<string, unknown>;
  outcome: Outcome;
  outcomeBasis: string;
  judged: boolean;
  counts: Partial<Record<Outcome, number>>;
  notes?: string;
  nextDue?: string;
  demo: boolean;
  createdAt: string;
  createdBy?: string;
  results: number;
  files: number;
  actions: number;
  revision: number;
  supersedes?: string;
  supersededBy?: string;
  amendmentReason?: string;
  /** How the data arrived: typed into the form, imported from a named file, or put in as demonstration data. */
  source: DataSource;
  sourceFile?: string;
}

export type DataSource = "form" | "file" | "demo" | "api";

export const SOURCE_LABEL: Record<DataSource, string> = {
  form: "Entered in the form",
  file: "Imported from a file",
  demo: "Demonstration data",
  api: "Sent by another system",
};

export interface TestResult {
  id: number;
  assetTag?: string;
  location?: string;
  value: number;
  specified?: number;
  attrs: Record<string, unknown>;
  judgedValue?: number;
  outcome: Outcome;
  thresholdLabel?: string;
  assetRemoved: boolean;
}

export interface ComparisonRow {
  key: string;
  assetTag?: string;
  location?: string;
  attrs: Record<string, unknown>;
  latest: number;
  latestOutcome: Outcome;
  prior?: number;
  priorOutcome?: Outcome;
  delta?: number;
}

export interface Comparison {
  prior: {
    id: string;
    reference: string;
    performedOn: string;
    outcome: Outcome;
    counts: Partial<Record<Outcome, number>>;
  };
  matched: number;
  onlyLatest: number;
  onlyPrior: number;
  meanLatest?: number;
  meanPrior?: number;
  worsened: number;
  improved: number;
  rows: ComparisonRow[];
}

export interface TestFile {
  id: string;
  objectId: string;
  contentType?: string;
  bytes?: number;
  note?: string;
  createdAt: string;
}

export interface TestRecordDetail {
  record: TestRecord & { thresholdsUsed: Threshold[] };
  results: TestResult[];
  files: TestFile[];
  comparison: Comparison | null;
  /** The readings this record wrote for the rules and models, and their measurement. */
  readingsWritten: { count: number; measurement?: string };
}

export const ACTION_KINDS = ["action_taken", "comment", "retest_planned", "work_order_raised"] as const;
export type ActionKind = (typeof ACTION_KINDS)[number];

export const ACTION_LABEL: Record<ActionKind, string> = {
  action_taken: "Action taken",
  comment: "Comment",
  retest_planned: "Re-test planned",
  work_order_raised: "Work order raised",
};

export interface TestAction {
  id: string;
  kind: ActionKind;
  body: string;
  assetTags: string[];
  createdAt: string;
  createdBy?: string;
  revision: number;
  workOrderId?: string;
  workOrderNumber?: string;
  workOrderStatus?: string;
}

export interface LogEntry {
  id: number;
  action: string;
  label: string;
  at: string;
  by?: string;
  before?: Record<string, unknown> | null;
  after?: Record<string, unknown> | null;
  recordId?: string;
  reference?: string;
  revision?: number;
  title?: string;
  testType?: string;
}

export interface Revision {
  id: string;
  revision: number;
  outcome: Outcome;
  counts: Partial<Record<Outcome, number>>;
  amendmentReason?: string;
  source: DataSource;
  sourceFile?: string;
  createdAt: string;
  createdBy?: string;
  supersededBy?: string;
  results: number;
}

export interface TestHistory {
  reference: string;
  revisions: Revision[];
  actions: TestAction[];
  log: LogEntry[];
}

export interface AssetHistoryPoint {
  recordId: string;
  reference: string;
  revision: number;
  performedOn: string;
  demo: boolean;
  value: number;
  specified?: number;
  judgedValue?: number;
  outcome: Outcome;
  thresholdLabel?: string;
}

/** One line saying what a log entry did, from what the platform recorded about it. */
export function describeLog(e: LogEntry): string {
  const a = e.after ?? {};
  const b = e.before ?? {};
  switch (e.action) {
    case "test_record.created":
      return `${t("Recorded {reference} — {outcome}", { reference: String(a.reference ?? ""), outcome: t(OUTCOME_LABEL[(a.outcome as Outcome) ?? "not_assessed"]) })}${a.results ? t(", {n} results", { n: String(a.results) }) : ""}${a.source_file ? ` ${t("from {file}", { file: String(a.source_file) })}` : a.source === "demo" ? ` ${t("(demonstration data)")}` : ""}`;
    case "test_record.revised":
      return `${t("Revision {n}: {before} → {after}. Reason: {reason}", {
        n: String(a.revision),
        before: t(OUTCOME_LABEL[(b.outcome as Outcome) ?? "not_assessed"]),
        after: t(OUTCOME_LABEL[(a.outcome as Outcome) ?? "not_assessed"]),
        reason: String(a.reason ?? "—"),
      })}${a.source_file ? ` · ${t("from {file}", { file: String(a.source_file) })}` : ""}`;
    case "test_record.file_attached":
      return `${t("Attached {name}", { name: a.name != null ? String(a.name) : t("a file") })}${a.bytes ? ` (${Math.max(1, Math.round(Number(a.bytes) / 1024))} kB)` : ""}`;
    case "test_type.thresholds_set":
      return t("Thresholds for {test} set — {basis}", {
        test: String(a.key ?? e.testType),
        basis: String(a.basis ?? ""),
      });
    default:
      return String(a.body ?? e.label);
  }
}

/** Rows of a spreadsheet (from CSV text or an Excel sheet) as the lines `parseResults` reads. */
export function rowsToText(rows: readonly (readonly unknown[])[]): string {
  return rows.map((r) => r.map((c) => (c == null ? "" : String(c).replace(/[,;\t\r\n]/g, " "))).join(",")).join("\n");
}

export interface LatestTest {
  latest: TestRecord | null;
  results: TestResult[];
  comparison: Comparison | null;
  thresholdsUsed: Threshold[];
}

export interface TypeSummary {
  testType: string;
  name: string;
  frameworks: string[];
  mode: TestMode;
  intervalDays?: number;
  intervalStatus: "proposed" | "agreed";
  records: number;
  last: TestRecord | null;
  nextDue?: string;
  state: DueState;
}

export interface TestingSummary {
  types: TypeSummary[];
  latestOutcomes: Record<Outcome, number>;
  overdue: number;
  dueSoon: number;
  neverTested: number;
}

export interface NewTestInput {
  testType: string;
  performedOn: string;
  title?: string;
  performedBy?: string;
  method?: string;
  device?: string;
  scope?: string;
  summary: Record<string, string | number>;
  results: {
    assetTag?: string;
    location?: string;
    value: number;
    specified?: number;
    attrs?: Record<string, string>;
  }[];
  /** `not_assessed` only for a judged test filed for information, with its basis (build 7) */
  outcome?: "pass" | "attention" | "fail" | "not_assessed";
  outcomeBasis?: string;
  notes?: string;
  nextDue?: string;
  source?: "form" | "file";
  sourceFile?: string;
}

/* ---- reading results typed or pasted ----------------------------------------------- */

export interface ParsedResults {
  rows: NewTestInput["results"];
  errors: string[];
}

/**
 * Results pasted from a contractor's spreadsheet: one per line,
 * `asset tag or location, value[, specified]`. A first line that is a header
 * is skipped; a line that does not read is reported by number, never guessed.
 * A first column that is not an asset tag is taken as a location.
 */
export function parseResults(text: string, knownTags?: ReadonlySet<string>): ParsedResults {
  const rows: NewTestInput["results"] = [];
  const errors: string[] = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((line, i) => {
    const raw = line.trim();
    if (!raw) return;
    const cells = raw.split(/[,;\t]/).map((c) => c.trim());
    const value = Number(cells[1]?.replace(/\s/g, ""));
    if (i === 0 && Number.isNaN(value)) return; // a header
    if (!cells[0] || !Number.isFinite(value)) {
      errors.push(
        t("line {n}: expected “asset or location, value”, got “{text}”", { n: i + 1, text: raw.slice(0, 60) }),
      );
      return;
    }
    const specified = cells[2] ? Number(cells[2].replace(/\s/g, "")) : undefined;
    if (specified !== undefined && !(Number.isFinite(specified) && specified > 0)) {
      errors.push(t("line {n}: the specified value “{value}” is not a positive number", { n: i + 1, value: cells[2] }));
      return;
    }
    const isTag = knownTags ? knownTags.has(cells[0]) : true;
    rows.push({
      ...(isTag ? { assetTag: cells[0] } : { location: cells[0] }),
      value,
      ...(specified ? { specified } : {}),
    });
  });
  return { rows, errors };
}

/* ---- search ---------------------------------------------------------------------------- */

export interface RecordFilters {
  q: string;
  testType: string | null;
  outcome: Outcome | null;
  framework: string | null;
  from?: string;
  to?: string;
  demo: "all" | "real" | "demo";
  /**
   * Who carried the test out — the service provider, matched exactly.
   *
   * `NO_PROVIDER` selects the records that name nobody, which is a question
   * worth being able to ask: a test result with no one against it cannot be
   * queried, repeated or challenged later, and those records are invisible
   * in a list sorted by provider.
   */
  provider: string | null;
}

/** The filter value that means "no service provider was recorded". */
export const NO_PROVIDER = "\u0000none";

export function matchesRecord(r: TestRecord, f: RecordFilters): boolean {
  if (f.testType && r.testType !== f.testType) return false;
  if (f.outcome && r.outcome !== f.outcome) return false;
  if (f.framework && !r.frameworks.some((x) => x.toLowerCase().includes(f.framework!.toLowerCase()))) return false;
  if (f.from && r.performedOn < f.from) return false;
  if (f.to && r.performedOn > f.to) return false;
  if (f.demo === "real" && r.demo) return false;
  if (f.demo === "demo" && !r.demo) return false;
  if (f.provider === NO_PROVIDER) {
    if ((r.performedBy ?? "").trim() !== "") return false;
  } else if (f.provider && (r.performedBy ?? "").trim() !== f.provider) return false;
  const q = f.q.trim().toLowerCase();
  if (
    q &&
    ![
      r.reference,
      r.title,
      r.testTypeName,
      r.performedBy,
      r.scope,
      r.device,
      r.method,
      r.notes,
      r.outcomeBasis,
      ...r.frameworks,
    ].some((x) => x?.toLowerCase().includes(q))
  )
    return false;
  return true;
}

/**
 * Every service provider that appears in a set of records, and how many
 * records name nobody.
 *
 * Distinct on the exact string, deliberately: "Gulf Testing" and "Gulf
 * Testing Co." are two entries here because they are two entries in the
 * records, and quietly merging them would hide a naming problem the engineer
 * is the only one who can fix. Sorted by name rather than by frequency, so
 * the list reads the same every time it is opened.
 */
export function providersOf(records: readonly Pick<TestRecord, "performedBy">[]): {
  names: string[];
  unnamed: number;
} {
  const names = new Set<string>();
  let unnamed = 0;
  for (const r of records) {
    const name = (r.performedBy ?? "").trim();
    if (name) names.add(name);
    else unnamed += 1;
  }
  return { names: [...names].sort((a, b) => a.localeCompare(b)), unnamed };
}

/** The regulators a set of test types cites, for a filter: whichever of ICAO, GACA and FAA appear. */
export function frameworksOf(types: readonly Pick<TestType, "frameworks">[]): string[] {
  const all = types.flatMap((ty) => ty.frameworks).join(" ");
  return ["ICAO", "GACA", "FAA", "ASTM"].filter((k) => all.includes(k));
}

/** Which thresholds a verdict rests on are still proposed — said beside it, every time. */
export function proposedThresholds(ts: readonly Threshold[]): boolean {
  return ts.some((th) => th.status !== "agreed");
}

export interface AttentionItem {
  testType: string;
  name: string;
  why: string;
  severity: "fail" | "attention" | "due";
  recordId?: string;
}

/** What needs somebody: the latest test of each type that did not pass, and every type that is late or never done. */
export function attentionList(summary: TestingSummary): AttentionItem[] {
  const out: AttentionItem[] = [];
  for (const ty of summary.types) {
    if (ty.last?.outcome === "fail")
      out.push({
        testType: ty.testType,
        name: ty.name,
        severity: "fail",
        recordId: ty.last.id,
        why: `${ty.last.reference}: ${ty.last.outcomeBasis}`,
      });
    else if (ty.last?.outcome === "attention")
      out.push({
        testType: ty.testType,
        name: ty.name,
        severity: "attention",
        recordId: ty.last.id,
        why: `${ty.last.reference}: ${ty.last.outcomeBasis}`,
      });
    if (ty.state === "overdue")
      out.push({
        testType: ty.testType,
        name: ty.name,
        severity: "due",
        recordId: ty.last?.id,
        why: t("Due {due}; not tested since {last}.", { due: String(ty.nextDue), last: String(ty.last?.performedOn) }),
      });
    else if (ty.state === "never_tested")
      out.push({
        testType: ty.testType,
        name: ty.name,
        severity: "due",
        why: t("No test of this type has been recorded at this site."),
      });
  }
  const rank = { fail: 0, attention: 1, due: 2 };
  return out.sort((a, b) => rank[a.severity] - rank[b.severity]);
}

function cell(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  const safe = /^[=+\-@\t\r]/.test(s) ? `'${s}` : s;
  return /[",\n\r]/.test(safe) ? `"${safe.replace(/"/g, '""')}"` : safe;
}

export function recordsCsv(records: readonly TestRecord[]): string {
  const head = [
    "Reference",
    "Test",
    "Title",
    "Performed on",
    "Performed by",
    "Scope",
    "Outcome",
    "Basis",
    "Pass",
    "Attention",
    "Action",
    "Not assessed",
    "Next due",
    "Frameworks",
    "Demonstration",
  ];
  const lines = records.map((r) =>
    [
      r.reference,
      r.testTypeName,
      r.title,
      r.performedOn,
      r.performedBy,
      r.scope,
      OUTCOME_LABEL[r.outcome],
      r.outcomeBasis,
      r.counts.pass,
      r.counts.attention,
      r.counts.fail,
      r.counts.not_assessed,
      r.nextDue,
      r.frameworks.join(" | "),
      r.demo ? "yes" : "",
    ]
      .map(cell)
      .join(","),
  );
  return [head.join(","), ...lines].join("\r\n") + "\r\n";
}

/* ---- consumption ------------------------------------------------------------------------- */

export interface ConsumptionPoint {
  assetTag: string;
  assetType: string;
  assetTypeName: string;
  role?: string;
  serves?: string;
  measurement: string;
  measurementName: string;
  unit?: string;
  period: string;
  consumed: number;
  resets: number;
  readings: number;
  /** Where the readings came from — a meter feed's connector, a hand reading, a seeded history. */
  sources?: string[];
}

export interface SeriesPoint {
  period: string;
  value: number;
  average: number | null;
}

/** Daily totals across meters for one measurement, with a trailing average over `window` periods that have data. */
export function totalSeries(points: readonly ConsumptionPoint[], measurement: string, window = 7): SeriesPoint[] {
  const byPeriod = new Map<string, number>();
  for (const p of points)
    if (p.measurement === measurement) byPeriod.set(p.period, (byPeriod.get(p.period) ?? 0) + p.consumed);
  const sorted = [...byPeriod].sort(([a], [b]) => a.localeCompare(b));
  return sorted.map(([period, value], i) => {
    const slice = sorted.slice(Math.max(0, i - window + 1), i + 1).map(([, v]) => v);
    return {
      period,
      value,
      average: slice.length >= Math.min(window, 3) ? slice.reduce((a, b) => a + b, 0) / slice.length : null,
    };
  });
}

export interface MeterComparison {
  assetTag: string;
  label: string;
  unit?: string;
  recent: number | null;
  previous: number | null;
  change: number | null;
  recentDays: number;
  previousDays: number;
}

/**
 * Each meter's last `days` against the `days` before. The change compares the
 * average per day with readings in each window, so a meter silent for part of
 * one window is not reported as having halved; a window with no readings is
 * null, not zero, and a change needs both.
 */
export function compareWindows(
  points: readonly ConsumptionPoint[],
  measurement: string,
  end: Date,
  days = 30,
): MeterComparison[] {
  const cut = (d: number) => new Date(end.getTime() - d * 86_400_000).toISOString().slice(0, 10);
  const recentFrom = cut(days);
  const previousFrom = cut(days * 2);
  const meters = new Map<string, MeterComparison>();
  for (const p of points) {
    if (p.measurement !== measurement) continue;
    const m =
      meters.get(p.assetTag) ??
      ({
        assetTag: p.assetTag,
        label: [p.assetTag, p.serves ?? p.role].filter(Boolean).join(" · "),
        unit: p.unit,
        recent: null,
        previous: null,
        change: null,
        recentDays: 0,
        previousDays: 0,
      } as MeterComparison);
    if (p.period > recentFrom) {
      m.recent = (m.recent ?? 0) + p.consumed;
      m.recentDays += 1;
    } else if (p.period > previousFrom) {
      m.previous = (m.previous ?? 0) + p.consumed;
      m.previousDays += 1;
    }
    meters.set(p.assetTag, m);
  }
  return (
    [...meters.values()]
      // Per day with readings, not raw totals: a window a meter was silent for half of is not half the consumption.
      .map((m) => {
        const r = m.recent != null && m.recentDays ? m.recent / m.recentDays : null;
        const p = m.previous != null && m.previousDays ? m.previous / m.previousDays : null;
        return { ...m, change: r != null && p ? (100 * (r - p)) / p : null };
      })
      .sort((a, b) => (b.recent ?? 0) - (a.recent ?? 0))
  );
}
